import { useEffect } from "react";
import { Pause, Play, Square, Zap } from "lucide-react";
import LoadingOverlay from "../../components/LoadingOverlay";
import { collapseDuplicateEvents, eventCopy, eventTone, formatDateLong, formatDuration, formatTime } from "./formatters";

const COLORS = {
  ACTIVE: "#2b6df2",
  MANUAL_PAUSE: "#f59e0b",
  AUTO_PAUSE: "#7c3aed",
};

export default function SessionDetailDrawer({ session, events = [], loading, onClose }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!session) return null;
  const total = (session.intervals || []).reduce((sum, item) => sum + (item.durationSeconds || 0), 0);
  const timeline = collapseDuplicateEvents(events);

  return (
    <div className="pa-drawer-backdrop" onMouseDown={onClose} role="presentation">
      <aside
        className="pa-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Session detail"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pa-drawer__head">
          <div>
            <h2 className="pa-section__title">Session Detail</h2>
            <p className="pa-section__copy">{formatDateLong(session.startTime)}</p>
          </div>
          <button type="button" className="pa-drawer__close" aria-label="Close session detail" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="pa-metrics">
          <div className="pa-metric">
            <span>Start</span>
            <strong>{formatTime(session.startTime)}</strong>
          </div>
          <div className="pa-metric">
            <span>End</span>
            <strong>{formatTime(session.endTime)}</strong>
          </div>
          <div className="pa-metric">
            <span>Active time</span>
            <strong>{formatDuration(session.activeSeconds)}</strong>
          </div>
          <div className="pa-metric">
            <span>Paused</span>
            <strong>{formatDuration(session.pausedSeconds)}</strong>
          </div>
        </div>

        <div className="pa-breakdown">
          {(session.intervals || []).map((item) => (
            <span
              key={item.intervalId}
              style={{
                width: `${total ? (item.durationSeconds / total) * 100 : 0}%`,
                background: COLORS[item.intervalType] || "#cbd5e1",
              }}
            />
          ))}
        </div>
        <div className="pa-legend" style={{ justifyContent: "flex-start", marginTop: 0 }}>
          <span><i className="pa-dot" style={{ background: COLORS.ACTIVE }} /> Active</span>
          <span><i className="pa-dot" style={{ background: COLORS.MANUAL_PAUSE }} /> Manual pause</span>
          <span><i className="pa-dot" style={{ background: COLORS.AUTO_PAUSE }} /> Auto-paused</span>
        </div>

        <div className="pa-timeline-wrap">
          <LoadingOverlay show={loading} label="Loading events..." />
          <ul className="pa-timeline">
          {timeline.map((event) => (
            <li key={event.eventId}>
              <span className={`pa-event-icon pa-event-icon--${eventTone(event.eventType)}`}>
                <EventIcon type={event.eventType} />
              </span>
              <div>
                <p className="pa-event-title">{eventCopy(event.eventType)}</p>
                <p className="pa-event-time">{formatTime(event.eventTime, { seconds: true })}</p>
                {event.comment && event.comment !== "LOGOUT" ? (
                  <p className="pa-comment">{event.comment}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        </div>
      </aside>
    </div>
  );
}

function EventIcon({ type }) {
  if (type === "PAUSE") return <Pause size={14} />;
  if (type === "STOP" || type === "LOGOUT") return <Square size={12} />;
  if (type === "AUTO_PAUSE") return <Zap size={14} />;
  return <Play size={14} />;
}
